import { Box, Button, Card, Grid, Stack, Typography, formatPhoneNumber, useTheme } from "@compass/core";
import InsertEmoticonIcon from "@mui/icons-material/InsertEmoticon";
import { IconButton, Popover } from "@mui/material";
import { Input } from "components";
import EmojiPicker, { EmojiClickData } from "emoji-picker-react";
import { SendMessageRequestDto, TextingChatHubAction, TextingConversation, TextingMessage, TextingOptInDto } from "generated/openApi";
import produce from "immer";
import { ConnectionStatus } from "pages/texting/ConnectionStatus";
import { useTextingChatHubConnection } from "pages/texting/useTextingChatHubConnection";
import { useEffect, useRef, useState } from "react";
import ApiService from "services/apiService";
import { TextingMessageBubble } from "./TextingMessageBubble";

export const ChatScreen = (props: { conversationId: number | undefined }) => {
  const theme = useTheme();
  const [conversation, setConversation] = useState<TextingConversation>();
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const [message, setMessage] = useState<string>("");
  const [optedInNumbers, setOptedInNumbers] = useState<TextingOptInDto[]>();
  const [emojiAnchorEl, setEmojiAnchorEl] = useState<HTMLElement | null>(null);
  const emojiPickerOpen = Boolean(emojiAnchorEl);

  const { textingChatHubConnection, connectionStatus } = useTextingChatHubConnection({
    conversationId: props.conversationId,
  });

  useEffect(() => {
    if (props.conversationId) {
      ApiService.textingConversations.get(props.conversationId).then((response) => {
        setConversation(response);
        scrollBottom();
      });

      Promise.all([
        ApiService.textingConversations.getOptedInStudentNumbers(),
        ApiService.textingConversations.getOptedInParentNumbers(),
      ]).then(([studentNumbers, parentNumbers]) => {
        setOptedInNumbers([...studentNumbers, ...parentNumbers]);
      });
    }
  }, [props.conversationId]);

  // handle incoming message
  useEffect(() => {
    if (!textingChatHubConnection) return;

    const handleIncomingMessage = (newTextingMessageJsonString: string) => {
      const newTextingMessage = JSON.parse(newTextingMessageJsonString) as TextingMessage;
      

      if (conversation && newTextingMessage.textingConversationId === conversation.id) {
        setConversation(
          produce(conversation, (draft) => {
            draft!.textingMessages!.push(newTextingMessage);
          })
        );

        // scrollBottom();
      }
    };


    textingChatHubConnection.on(TextingChatHubAction[TextingChatHubAction.BroadcastNewMessage], handleIncomingMessage);

    return () => {
      textingChatHubConnection.off(
        TextingChatHubAction[TextingChatHubAction.BroadcastNewMessage],
        handleIncomingMessage
      );
    };
  }, [conversation, textingChatHubConnection, messagesContainerRef]);

  const renderTextMessage = (textMessage: TextingMessage, index: number) => {
    return (
      <Grid item xs={12} key={`${index}:${textMessage.sid}`} sx={{ display: "flex" }}>
        <TextingMessageBubble
          message={textMessage.messageBody}
          isSender={!textMessage.inbound}
          dateSent={textMessage.dateSent}
        />
      </Grid>
    );
  };

  function scrollBottom() {
    setTimeout(() => {
      if (messagesContainerRef.current)
        messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
    }, 0);

    setTimeout(() => {
      if (messagesContainerRef.current)
        messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
    }, 500);
  }

  const markAsRead = () => {
    console.log("marking as read...");
  };

  const handleArchive = () => {
    console.log("archiving...");
  };

  const sendMessage = () => {
    const request: SendMessageRequestDto = {
      conversationId: props.conversationId!,
      messageBody: message,
    };

    ApiService.textingConversations.sendMessage(request).then((sendResponse) => {
      setMessage("");
      ApiService.textingConversations.get(props.conversationId).then((response) => {
        setConversation(response);
        scrollBottom();
      });
    });
  };

  const normalizePhoneNumber = (phoneNumber?: string | null) => {
    const digits = phoneNumber?.replace(/\D/g, "") || "";

    if (digits.length === 11 && digits.startsWith("1")) {
      return digits.substring(1);
    }

    return digits;
  };

  const addEmoji = (emojiData: EmojiClickData) => {
    setMessage((current) => `${current || ""}${emojiData.emoji}`);
  };

  const header = () => {
    return (
      <Stack direction="row" justifyContent={"space-between"} alignItems={"center"}>
        <Stack>
          {/* <Typography variant="h6">{conversation?.id}</Typography> */}
          <Typography variant="h6">
            {conversation?.person?.fullName} - {conversation?.personId} ({conversation?.personType?.name})
          </Typography>
          <Typography variant="body1">{formatPhoneNumber(conversation?.destinationPhoneNumber)}</Typography>
          {/* <Typography variant="h6">
            CurrentTopic:
            {conversation?.currentTopic}
          </Typography> */}
          <Box>
            <ConnectionStatus status={connectionStatus} />
          </Box>
        </Stack>

        {/* <Stack direction="row" spacing="md" justifyContent="flex-end">
          <Button onClick={() => markAsRead()} variant="outlined">
            Mark as Unread
          </Button>
          <Button onClick={() => handleArchive()} variant="contained" color="error">
            Archive
          </Button>
        </Stack> */}
      </Stack>
    );
  };

  const messagesContainer = () => {
    return (
      <Stack overflow="auto">{conversation?.textingMessages?.map((m, index) => renderTextMessage(m, index))}</Stack>
    );
  };

  const sendTextContainer = () => {
    const conversationPhoneNumber = normalizePhoneNumber(conversation?.destinationPhoneNumber);
    const canSendMessage =
      optedInNumbers?.some((n) => normalizePhoneNumber(n.phoneNumber) === conversationPhoneNumber) &&
      message &&
      message.trim().length > 0;

    return (
      <Stack
        direction="row"
        alignItems="stretch"
        sx={{
          p: 2,
          bgcolor: theme.palette.background.paper,
        }}
      >
        <Input
          multiline
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          sx={{
            flexGrow: 1,
            "& .MuiOutlinedInput-root": {
              borderTopRightRadius: 0,
              borderBottomRightRadius: 0,
              bgcolor: theme.palette.background.paper,
              "& fieldset": {
                borderRightWidth: 0,
              },
              "&.Mui-focused fieldset": {
                borderRightWidth: 0,
              },
            },
          }}
        />

        <IconButton
          aria-label="Add emoji"
          color="primary"
          onClick={(e) => setEmojiAnchorEl(e.currentTarget)}
          sx={{
            border: `1px solid ${theme.palette.border.main}`,
            borderRadius: 0,
            bgcolor: theme.palette.background.paper,
          }}
        >
          <InsertEmoticonIcon />
        </IconButton>

        <Button
          variant="contained"
          onClick={sendMessage}
          disabled={!canSendMessage}
          sx={{
            minWidth: 84,
            borderTopLeftRadius: 0,
            borderBottomLeftRadius: 0,
          }}
        >
          Send
        </Button>

        <Popover
          open={emojiPickerOpen}
          anchorEl={emojiAnchorEl}
          onClose={() => setEmojiAnchorEl(null)}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
          transformOrigin={{ vertical: "bottom", horizontal: "right" }}
        >
          <EmojiPicker onEmojiClick={addEmoji} height={400} width={350} />
        </Popover>
      </Stack>
    );
  };

  return (
    <Card style={{ height: "100%" }} bodySx={{ p: 0 }}>
      <Stack height="100%">
        <Box borderBottom={`1px solid ${theme.palette.border.main}`} p={4}>
          {header()}
        </Box>

        <Box
          ref={messagesContainerRef}
          borderBottom={`1px solid ${theme.palette.border.main}`}
          flexGrow={1}
          p={4}
          overflow="auto"
        >
          {messagesContainer()}
        </Box>

        <Box>{sendTextContainer()}</Box>
      </Stack>
    </Card>
  );
};
